import { motion } from 'framer-motion'
import { 
  Book, Search, Code, Terminal, Key, Webhook,
  ChevronRight, Copy, Check, ExternalLink,
  FileJson, Shield, Settings
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import SEO, { schemas } from '../components/SEO'

// Section anchors; titles/item names come from docs.json (sidebar.<id>) in the same order.
const sidebarGroups = [
  { id: 'start', hrefs: ['#intro', '#setup', '#auth'] },
  { id: 'api', hrefs: ['#sessions', '#users', '#groups', '#reports'] },
  { id: 'webhooks', hrefs: ['#webhook-intro', '#events', '#signature'] },
  { id: 'sdk', hrefs: ['#js-sdk', '#py-sdk', '#go-sdk'] },
]

const codeExample = `// API Anahtarı ile Kimlik Doğrulama
const cmdesk = require('@cmdesk/sdk');

const client = new cmdesk.Client({
  apiKey: 'your_api_key_here',
  region: 'eu-central'
});

// Yeni bir oturum başlat
const session = await client.sessions.create({
  targetDeviceId: 'device_123',
  options: {
    quality: 'high',
    recordSession: true
  }
});

console.log('Oturum ID:', session.id);`

const webhookExample = `{
  "event": "session.connected",
  "timestamp": "2026-03-28T10:30:00Z",
  "data": {
    "session_id": "sess_abc123",
    "user_id": "usr_xyz789",
    "device_id": "dev_def456",
    "ip_address": "203.0.113.1",
    "started_at": "2026-03-28T10:30:00Z"
  }
}`

export default function Docs() {
  const { t } = useTranslation(['docs', 'common'])
  const [copied, setCopied] = useState(false)
  const [activeSection, setActiveSection] = useState('intro')
  const [searchQuery, setSearchQuery] = useState('')

  const sidebarItems = sidebarGroups.map((group) => ({
    title: t(`sidebar.${group.id}.title`),
    items: (t(`sidebar.${group.id}.items`, { returnObjects: true }) as string[]).map((name, i) => ({
      name,
      href: group.hrefs[i],
    })),
  }))

  const copyCode = () => {
    navigator.clipboard.writeText(codeExample)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      
      className="pt-24 pb-24"
    >
      <SEO
        title={t('seo.title')}
        description={t('seo.description')}
        keywords={t('seo.keywords')}
        canonical="/docs"
        structuredData={schemas.breadcrumb([{ name: t('common:pages.home'), url: '/' }, { name: t('breadcrumbName'), url: '/docs' }])}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              {/* Search */}
              <div className="relative mb-6">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type="text"
                  placeholder={t('searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 glass rounded-lg text-sm text-white placeholder-gray-500 focus:outline-hidden focus:border-primary"
                />
              </div>

              {/* Navigation */}
              <nav className="space-y-6">
                {sidebarItems.map((section) => (
                  <div key={section.title}>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                      {section.title}
                    </h3>
                    <ul className="space-y-1">
                      {section.items.map((item) => (
                        <li key={item.name}>
                          <button
                            onClick={() => setActiveSection(item.href.slice(1))}
                            className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                              activeSection === item.href.slice(1)
                                ? 'bg-primary/20 text-primary'
                                : 'text-gray-400 hover:text-white hover:bg-white/5'
                            }`}
                          >
                            {item.name}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </nav>

              {/* API Status */}
              <div className="mt-8 p-4 glass rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
                  <span className="text-white text-sm font-medium">{t('status.active')}</span>
                </div>
                <p className="text-gray-500 text-xs">{t('status.updated')}</p>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {/* Header */}
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                <Book className="w-4 h-4" />
                <ChevronRight className="w-4 h-4" />
                <span>{t('trail.root')}</span>
                <ChevronRight className="w-4 h-4" />
                <span className="text-primary">{t('trail.current')}</span>
              </div>

              <h1 className="text-4xl font-display font-bold text-white mb-6">
                {t('title')}
              </h1>
              <p className="text-gray-400 text-lg mb-8 leading-relaxed">
                {t('intro')}
              </p>

              {/* Quick Links */}
              <div className="grid md:grid-cols-3 gap-4 mb-12">
                <a href="#auth" className="glass rounded-xl p-4 hover:bg-white/10 transition-colors group">
                  <Key className="w-8 h-8 text-primary mb-3" />
                  <h3 className="text-white font-semibold mb-1 group-hover:text-primary transition-colors">{t('quickLinks.auth.title')}</h3>
                  <p className="text-gray-500 text-sm">{t('quickLinks.auth.text')}</p>
                </a>
                <a href="#sessions" className="glass rounded-xl p-4 hover:bg-white/10 transition-colors group">
                  <Terminal className="w-8 h-8 text-secondary mb-3" />
                  <h3 className="text-white font-semibold mb-1 group-hover:text-secondary transition-colors">{t('quickLinks.sessions.title')}</h3>
                  <p className="text-gray-500 text-sm">{t('quickLinks.sessions.text')}</p>
                </a>
                <a href="#webhook-intro" className="glass rounded-xl p-4 hover:bg-white/10 transition-colors group">
                  <Webhook className="w-8 h-8 text-accent mb-3" />
                  <h3 className="text-white font-semibold mb-1 group-hover:text-accent transition-colors">{t('quickLinks.webhooks.title')}</h3>
                  <p className="text-gray-500 text-sm">{t('quickLinks.webhooks.text')}</p>
                </a>
              </div>

              {/* Code Example */}
              <div className="mb-12">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <Code className="w-5 h-5 text-primary" />
                    {t('quickStart')}
                  </h2>
                  <button
                    onClick={copyCode}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-colors text-sm"
                  >
                    {copied ? <Check className="w-4 h-4 text-success" /> : <Copy className="w-4 h-4" />}
                    {copied ? t('copied') : t('copy')}
                  </button>
                </div>
                <div className="glass rounded-xl overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-2 bg-white/5 border-b border-white/5">
                    <div className="w-3 h-3 rounded-full bg-danger" />
                    <div className="w-3 h-3 rounded-full bg-accent" />
                    <div className="w-3 h-3 rounded-full bg-success" />
                    <span className="ml-4 text-gray-500 text-xs">example.js</span>
                  </div>
                  <pre className="p-4 overflow-x-auto">
                    <code className="text-sm text-gray-300 font-mono">{codeExample}</code>
                  </pre>
                </div>
              </div>

              {/* Webhook Example */}
              <div className="mb-12">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <FileJson className="w-5 h-5 text-secondary" />
                  {t('webhookTitle')}
                </h2>
                <div className="glass rounded-xl overflow-hidden">
                  <pre className="p-4 overflow-x-auto">
                    <code className="text-sm text-gray-300 font-mono">{webhookExample}</code>
                  </pre>
                </div>
              </div>

              {/* Rate Limits */}
              <div className="glass rounded-xl p-6 mb-12">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Settings className="w-5 h-5 text-accent" />
                  {t('rateLimits.title')}
                </h2>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">{t('rateLimits.plan')}</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">{t('rateLimits.limit')}</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">{t('rateLimits.burst')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-white/5">
                        <td className="py-3 px-4 text-white">{t('rateLimits.free')}</td>
                        <td className="py-3 px-4 text-gray-400">{t('rateLimits.freeLimit')}</td>
                        <td className="py-3 px-4 text-gray-400">10</td>
                      </tr>
                      <tr className="border-b border-white/5">
                        <td className="py-3 px-4 text-white">{t('rateLimits.pro')}</td>
                        <td className="py-3 px-4 text-gray-400">{t('rateLimits.proLimit')}</td>
                        <td className="py-3 px-4 text-gray-400">100</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-white">{t('rateLimits.enterprise')}</td>
                        <td className="py-3 px-4 text-gray-400">{t('rateLimits.unlimited')}</td>
                        <td className="py-3 px-4 text-gray-400">1000</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Support */}
              <div className="glass rounded-xl p-6">
                <h2 className="text-xl font-bold text-white mb-4">{t('support.title')}</h2>
                <div className="flex flex-wrap gap-4">
                  <a
                    href="mailto:api@cmdesk.help"
                    className="flex items-center gap-2 px-4 py-2 bg-primary/20 text-primary rounded-lg hover:bg-primary/30 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    {t('support.api')}
                  </a>
                  <a
                    href="#"
                    className="flex items-center gap-2 px-4 py-2 bg-white/5 text-white rounded-lg hover:bg-white/10 transition-colors"
                  >
                    <Shield className="w-4 h-4" />
                    {t('support.security')}
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.div>
  )
}
